// lib/genericApi.ts
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig, AxiosInstance } from 'axios';
import { toast } from 'react-toastify';

// Utility function to extract path from URL
const extractPath = (url: string | undefined): string => {
  if (!url) return 'unknown';
  // Remove query parameters and hash, keep only the path
  const path = url.split('?')[0].split('#')[0];
  return path || 'unknown';
};

// Configuration interface for creating API instances
export interface ApiConfig {
  headerName?: string; // Custom header name (e.g., 'Jwt-Token', 'Token')
  getToken?: () => string | undefined; // Function to get token (e.g., from cookies, params, etc.)
  baseURL?: string;
  errorHandler?: (error: AxiosError, apiInstance: AxiosInstance) => void;
}

// Helper function to dismiss toasts while preserving push notification toasts
const dismissToastsPreservingPushNotifications = () => {
  // Check if there are any active push notification toasts
  const pushNotificationToasts = (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) 
    ? (window as any).__pushNotificationToastIds as Set<string>
    : new Set<string>();
  
  // If there are active push notification toasts, skip dismissing toasts entirely
  // to prevent push notification toasts from being dismissed
  if (pushNotificationToasts.size > 0) {
    return;
  }
  
  // Only dismiss toasts if there are no push notification toasts
  toast.dismiss();
};

// Create a generic API instance with custom configuration
export function createApiInstance(config: ApiConfig = {}): AxiosInstance {
  const {
    headerName = 'Token',
    getToken,
    baseURL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api',
    errorHandler,
  } = config;

  // Create Axios instance
  const apiInstance = axios.create({
    baseURL: baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor for custom headers
  apiInstance.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      // Skip dismissing toasts for push notification related API calls
      const isAuditApi = requestConfig.url?.includes('/v1/audit');
      const isPushNotificationApi = isAuditApi && 
                                    (typeof requestConfig.data === 'string' 
                                      ? requestConfig.data.includes('push_notification')
                                      : JSON.stringify(requestConfig.data || {}).includes('push_notification'));
      
      const isNotificationEndpoint = requestConfig.url?.includes('notification') || 
                                     requestConfig.url?.includes('firebase');
      
      if (!isPushNotificationApi && !isNotificationEndpoint) {
        dismissToastsPreservingPushNotifications();
      }

      // Get token from headers if already provided, or from getToken function
      const existingToken = requestConfig.headers?.[headerName] || 
                           requestConfig.headers?.[headerName.toLowerCase()];
      
      const token = existingToken || (getToken ? getToken() : undefined);
      
      if (token && requestConfig.headers) {
        requestConfig.headers[headerName] = `${token}`;
      }
      
      return requestConfig;
    },
    (error: AxiosError) => {
      return Promise.reject({
        message: 'Request configuration failed',
        details: error.message,
      });
    }
  );

  // Response interceptor for error handling
  apiInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      const { response, request, config } = error;
      let errorMessage = 'An unexpected error occurred';
      const statusCode = response?.status;
      const requestPath = extractPath(config?.url);

      const customError = {
        message: errorMessage,
        status: statusCode,
        details: response?.data || error.message,
      };

      if (response) {
        // Server responded with a status code
        if (statusCode === 401) {
          errorMessage = 'Unauthorized. Please check your credentials.';
        } else if (statusCode === 400 || statusCode === 404 || statusCode === 500) {
          errorMessage = (response.data as any)?.message || 
                        (response.data as any)?.error || 
                        'An error occurred. Please try again.';
        } else if (statusCode === 403) {
          errorMessage = (response.data as any)?.message || 
                        (response.data as any)?.error || 
                        'Access denied.';
        }
      } else if (request) {
        // Request was made but no response received (e.g., network error)
        errorMessage = 'No internet connection. Please check your network and try again.';
      } else {
        // Error setting up the request
        errorMessage = `Request Error: ${error.message}`;
      }

      customError.message = errorMessage;

      // Call custom error handler if provided
      if (errorHandler) {
        errorHandler(error, apiInstance);
      }

      // Log error for debugging
      console.log('API Error:', customError);

      // Reject with custom error
      return Promise.reject(customError);
    }
  );

  return apiInstance;
}

// Export default instance with standard Token header (for backward compatibility)
export default createApiInstance({
  headerName: 'Token',
  getToken: () => {
    // This would typically use getCookie from cookies-next
    // For now, return undefined - should be set by the caller
    return undefined;
  },
});

